import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import axios from "../../utils/publicAxios";
import "../../styles/features/events/GuestEventSignup.css";

export default function GuestEventSignup() {
  const { eventId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  const eventPreviewFromState = location.state?.eventPreview;

  const [loading, setLoading] = useState(true);
  const [event, setEvent] = useState(eventPreviewFromState || null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  const [form, setForm] = useState({
    firstName: "",
    lastName: "",
    birthDate: "",
    email: "",
    phone: "",
    beenBefore: "",
    acceptTerms: false,
    acceptParticipationRules: false,
  });

  useEffect(() => {
    const load = async () => {
      try {
        setLoading(true);

        // Jeśli nie masz eventu w state (np. user wszedł z linku), dociągnij event:
        if (!eventPreviewFromState) {
          const res = await axios.get(`/api/events/${eventId}`);
          setEvent(res.data);
        }
      } catch (e) {
        // jeśli nie masz jeszcze API — możesz tymczasowo nie blokować, ale lepiej pokazać błąd
        setError("Nie udało się pobrać danych wydarzenia.");
      } finally {
        setLoading(false);
      }
    };

    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventId]);

  const onChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm((f) => ({
      ...f,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const validate = () => {
    if (!form.firstName.trim()) return "Podaj imię.";
    if (!form.lastName.trim()) return "Podaj nazwisko.";
    if (!form.birthDate) return "Podaj datę urodzenia.";
    if (!/^\S+@\S+\.\S+$/.test(form.email)) return "Podaj poprawny email.";
    if (!form.phone.trim()) return "Podaj numer telefonu.";
    if (!["YES", "NO"].includes(form.beenBefore)) return "Zaznacz, czy byłeś/-aś już u nas na eventach.";
    if (!form.acceptTerms) return "Musisz zaakceptować regulamin.";
    if (!form.acceptParticipationRules) return "Musisz potwierdzić zapoznanie się z warunkami udziału.";
    return "";
  };

  const canSubmit = useMemo(() => {
    return (
      form.firstName.trim() &&
      form.lastName.trim() &&
      form.birthDate &&
      /^\S+@\S+\.\S+$/.test(form.email) &&
      form.phone.trim() &&
      ["YES", "NO"].includes(form.beenBefore) &&
      form.acceptTerms &&
      form.acceptParticipationRules
    );
  }, [form]);

  const submit = async (e) => {
    e.preventDefault();
    setError("");

    const v = validate();
    if (v) {
      setError(v);
      return;
    }

    try {
      setSubmitting(true);

      // Payload do backendu:
      const payload = {
        firstName: form.firstName.trim(),
        lastName: form.lastName.trim(),
        birthDate: form.birthDate,
        email: form.email.trim().toLowerCase(),
        phone: form.phone.trim(),
        beenBefore: form.beenBefore === "YES",
        acceptedTerms: true,
        acceptedParticipationRules: true,
      };

      const res = await axios.post(`/api/events/${eventId}/guest-reservations`, payload);

      navigate(`/events/${eventId}/signup/success`, {
        state: {
          reservationCode: res.data.reservationCode,
          expiresAt: res.data.expiresAt,
          email: payload.email,
          eventTitle: event?.title || eventPreviewFromState?.title,
        },
      });
    } catch (err) {
      setError(err?.response?.data?.message || "Nie udało się utworzyć rezerwacji. Spróbuj ponownie.");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="guestSignup page">
        <div className="guestSignup__container">
          <div className="guestSignup__skeleton" />
        </div>
      </div>
    );
  }

  if (!event) {
    return (
      <div className="guestSignup page">
        <div className="guestSignup__container">
          <h2>Więcej informacji o wydarzeniu wkrótce</h2>
          <button className="btn" onClick={() => navigate(-1)}>Wróć</button>
        </div>
      </div>
    );
  }

  return (
    <div className="guestSignup page">
      <div className="guestSignup__container">
        <button className="guestSignup__back" onClick={() => navigate(-1)}>← Wróć</button>

        <div className="guestSignup__header">
          <h1>Zapis na wydarzenie</h1>
          <p className="muted">
            {event.title} • {event.date} {event.time} • {event.city}
          </p>
          <div className="guestSignup__price">
            Cena: {event.price} PLN
          </div>
        </div>

        <form className="guestSignup__form" onSubmit={submit}>
          <div className="guestSignup__grid">
            <label>
              <input name="firstName" placeholder="Imię" value={form.firstName} onChange={onChange} />
            </label>

            <label>
              <input name="lastName" placeholder="Nazwisko" value={form.lastName} onChange={onChange} />
            </label>

            <label>
              <input type="email" placeholder="Email" name="email" value={form.email} onChange={onChange} />
            </label>

            <label>
              <input name="phone" placeholder="Numer telefonu" value={form.phone} onChange={onChange} />
            </label>

              <label>
              Data urodzenia
              <input type="date" name="birthDate" placeholder="Data urodzenia" value={form.birthDate} onChange={onChange} />
            </label>
          </div>

          <div className="guestSignup__section">
            <div className="guestSignup__question">
              Czy byłeś/-aś już u nas na eventach?
            </div>

            <div className="guestSignup__radios">
              <label className="radio">
                <input
                  type="radio"
                  name="beenBefore"
                  value="YES"
                  checked={form.beenBefore === "YES"}
                  onChange={onChange}
                />
                Tak
              </label>

              <label className="radio">
                <input
                  type="radio"
                  name="beenBefore"
                  value="NO"
                  checked={form.beenBefore === "NO"}
                  onChange={onChange}
                />
                Nie
              </label>
            </div>
          </div>

          <div className="guestSignup__section">
            <label className="check">
              <input
                type="checkbox"
                name="acceptTerms"
                checked={form.acceptTerms}
                onChange={onChange}
              />
              Akceptuję{" "}
              <a href="/privacy-policy" target="_blank" rel="noreferrer">
                regulamin
              </a>
              
            </label>

            <label className="check">
              <input
                type="checkbox"
                name="acceptParticipationRules"
                checked={form.acceptParticipationRules}
                onChange={onChange}
              />
              Zapoznałem/-am się z{" "}
              <a href="/terms" target="_blank" rel="noreferrer">
                warunkami udziału
              </a>
    
            </label>

            <p className="tiny">
              Po rezerwacji dostaniesz mail z instrukcją płatności. Masz 24h na opłacenie, inaczej rezerwacja wygaśnie.
            </p>
          </div>

          {error && <div className="error">{error}</div>}

          <button className="btnPrimary" disabled={!canSubmit || submitting}>
            {submitting ? "Rezerwuję..." : "Rezerwuje miejsce"}
          </button>
        </form>
      </div>
    </div>
  );
}
